import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FinanceRate } from '@kanda-libs/ks-frontend-services';

import { formatToCurrency, formatToPercentage } from 'utils';
import { getMinimumMonthlyForRates } from 'utils/FinanceRate-functions';

import { POINTS, SUBHEADINGS } from './constants';

import { FinanceFormValues, useFinanceFormContext } from '../../../context';
import { SplitKey } from '../useAPRs';

export interface APRButtonHook {
  buttonClassName: string;
  indicatorClassName: string;
  buttonId: string;
  title: string;
  subheading: string;
  onClick: () => void;
  minimumMonthlyCost: string;
  points: string[];
}

export default function useAPRButton(splitKey: SplitKey): APRButtonHook {
  const { job, allRates, termRef } = useFinanceFormContext();
  const { setValue } = useFormContext<FinanceFormValues>();
  const [apr, aprType] = useWatch<FinanceFormValues, ['apr', 'aprType']>({
    name: ['apr', 'aprType'],
  });

  const active = useMemo(
    () => apr === splitKey.apr && aprType === splitKey.financeType,
    [splitKey, apr, aprType],
  );

  const buttonClassName = useMemo(
    () =>
      [
        'p-4 md:p-6 border-2 rounded-xl transition-all flex flex-col group shadow-md transition-colors',
        active
          ? 'bg-purple-50 border-purple-400'
          : 'border-2 bg-neutral-000 border-neutral-300 hover:border-purple-200',
      ].join(' '),
    [active],
  );

  const indicatorClassName = useMemo(
    () =>
      [
        'w-6 h-6 min-w-6 min-h-6 border-2 rounded-full transition-all flex',
        active
          ? 'bg-purple-400 border-purple-400'
          : 'bg-neutral-000 border-neutral-300 group-hover:border-purple-200',
      ].join(' '),
    [active],
  );

  const buttonId = useMemo(
    () => `finance-${splitKey.apr}-${splitKey.financeType}`,
    [splitKey],
  );

  const title = useMemo(() => {
    if (splitKey.financeType === 'BUYNOW_PAYLATER') return 'Buy Now, Pay Later';
    return `${formatToPercentage(parseInt(splitKey.apr, 10))} APR`;
  }, [splitKey]);

  const subheading = useMemo(
    () => SUBHEADINGS[splitKey.financeType],
    [splitKey],
  );

  const minimumMonthly = useMemo(() => {
    if (!allRates || !job) return undefined;
    const total = job.total?.amount_vat_inclusive?.amount ?? 0;
    const deposit = job.deposit_value.amount;
    const depositType = job.deposit_type;
    const aprRates = allRates[`${splitKey.apr}-${splitKey.financeType}`];
    const rates = Object.keys(aprRates).reduce(
      (aprRatesArray: FinanceRate[], key) => {
        const durationRates = aprRates[key];
        return [...aprRatesArray, ...durationRates];
      },
      [],
    );
    return getMinimumMonthlyForRates(
      rates,
      total,
      deposit,
      depositType === 'no_deposit',
    );
  }, [job, allRates, splitKey]);

  const minimumMonthlyCost = useMemo(() => {
    if (!minimumMonthly) return 'From £--.--';
    return `From ${formatToCurrency(minimumMonthly)}*`;
  }, [minimumMonthly]);

  const maximumDuration = useMemo(() => {
    if (!allRates) return '--';
    const durations = Object.keys(
      allRates[`${splitKey.apr}-${splitKey.financeType}`],
    ).map((duration) => {
      if (splitKey.financeType === 'BUYNOW_PAYLATER') {
        const parts = duration.split('-');
        return parseInt(parts[1], 10);
      }
      return parseInt(duration, 10);
    });
    return Math.max(...durations).toString();
  }, [allRates, splitKey]);

  const fullDurations = useMemo(() => {
    if (!allRates) return '--';
    if (splitKey.financeType !== 'BUYNOW_PAYLATER') return '--';
    const durations = Object.keys(
      allRates[`${splitKey.apr}-${splitKey.financeType}`],
    )
      .map((duration) => {
        const parts = duration.split('-');
        return parseInt(parts[1], 10);
      })
      .filter((v, i, a) => a.indexOf(v) === i);
    if (durations.length === 1) return durations[0].toString();
    if (durations.length === 2) return durations.join(' or ');
    return [durations.slice(0, -1).join(', '), durations.slice(-1)].join(
      ' or ',
    );
  }, [allRates, splitKey]);

  const deferredDurations = useMemo(() => {
    if (!allRates) return '--';
    if (splitKey.financeType !== 'BUYNOW_PAYLATER') return '--';
    const durations = Object.keys(
      allRates[`${splitKey.apr}-${splitKey.financeType}`],
    )
      .map((duration) => {
        const parts = duration.split('-');
        return parseInt(parts[0], 10);
      })
      .filter((v, i, a) => a.indexOf(v) === i);
    if (durations.length === 1) return durations[0].toString();
    if (durations.length === 2) return durations.join(' or ');
    return [durations.slice(0, -1).join(', '), durations.slice(-1)].join(
      ' or ',
    );
  }, [allRates, splitKey]);

  const points = useMemo(() => {
    const base = POINTS[splitKey.financeType];
    return base.map((basePoint) =>
      basePoint
        .replaceAll('{{fullDurations}}', fullDurations)
        .replaceAll(
          '{{minimumMonthly}}',
          `${minimumMonthly ? formatToCurrency(minimumMonthly) : '--'}`,
        )
        .replaceAll('{{deferredDurations}}', deferredDurations)
        .replaceAll('{{apr}}', formatToPercentage(parseInt(splitKey.apr, 10)))
        .replaceAll('{{maxDuration}}', maximumDuration),
    );
  }, [
    fullDurations,
    minimumMonthly,
    deferredDurations,
    splitKey,
    maximumDuration,
  ]);

  const onClick = useCallback(() => {
    if (apr !== splitKey.apr || aprType !== splitKey.financeType) {
      setValue('apr', splitKey.apr);
      setValue('aprType', splitKey.financeType);
      setValue('duration', undefined);
      setValue('deferredDuration', undefined);
    }
    setTimeout(() => {
      const el = termRef.current;
      if (!el) return;
      const offset = window.innerWidth < 768 ? 154 : 121;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    }, 100);
  }, [apr, aprType, setValue, splitKey, termRef]);

  return {
    buttonClassName,
    indicatorClassName,
    buttonId,
    onClick,
    title,
    subheading,
    minimumMonthlyCost,
    points,
  };
}
